import React, { useState, useEffect } from "react";
import axios from "axios"; 
import { toast } from "react-toastify";
import { toWords } from "number-to-words";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faReceipt, 
  faPrint, 
  faPaperPlane, 
  faUserCircle, 
  faEnvelope, 
  faPhone,
  faUtensils,
  faCalculator,
  faCircleInfo ,
  faTag,
  faCircleCheck,
  faBuilding,
  faTable,
  faClock,
  faCalendarAlt,
  faCoins,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import generatePrintInvoiceHTML from "../../utils/printInvoice";
import { buildApiUrl } from "../../config/api.config";

const Invoice = ({ invoiceId, user, restaurantId }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        let activeRestaurantId = restaurantId || localStorage.getItem('currentRestaurantId');
        
        // Handle populated restaurantId object
        if (activeRestaurantId && typeof activeRestaurantId === 'object') {
            activeRestaurantId = activeRestaurantId._id;
        }
        if (!activeRestaurantId) {
          setError("Restaurant context missing.");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          buildApiUrl(`/invoice/admin/${invoiceId}?restaurantId=${activeRestaurantId}`),
          { headers: { 'auth-token': localStorage.getItem('token') } }
        );
        setInvoice(response.data);
        setLoading(false);
      } catch (err) {
        setError("Error fetching invoice data.");
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoiceId]);

  const printInvoice = () => {
    const printWindow = window.open("", "", "height=800,width=1200");
    const invoiceHTML = generatePrintInvoiceHTML(invoice);
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.print();
  };

  const sendInvoice = async () => {
    try {
      setIsSending(true);
      const activeRestaurantId = restaurantId || localStorage.getItem('currentRestaurantId');
      const response = await axios.post(
        buildApiUrl(`/users/send-invoice/${invoiceId}`),
        { userId: user, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      setIsSending(false);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error sending invoice');
      setIsSending(false);
    }
  };

  if (loading) return (
    <div className="tf-flex-center" style={{ padding: '6rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
       <div className="tf-spinner"></div>
       <p style={{ color: 'var(--tf-text-muted)' }}>Generating bill visualization...</p>
    </div>
  );
  
  if (error) return (
    <div className="tf-empty-state">
       <FontAwesomeIcon icon={faReceipt} size="3x" style={{ opacity: 0.1, marginBottom: '1.5rem' }} />
       <p>{error}</p>
    </div>
  );

  const layoutId = invoice.restaurantId?.invoiceSettings?.layoutId || '1';
  const primaryColor = invoice.restaurantId?.invoiceSettings?.primaryColor || 'var(--tf-primary)';
  const accentColor = invoice.restaurantId?.invoiceSettings?.accentColor || '#ff6b6b';

  const renderLayout1 = () => (
    <div className="tf-grid" style={{ gap: '2rem' }}>
      {/* Dual Column Info */}
      <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
         <div className="tf-card" style={{ padding: '1.5rem' }}>
            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: primaryColor, letterSpacing: '1px', marginBottom: '1.25rem' }}>
               <FontAwesomeIcon icon={faUserCircle} /> Recipient
            </h4>
            <div className="tf-grid" style={{ gap: '1rem' }}>
               <div className="tf-flex" style={{ gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faUserCircle} style={{ color: 'var(--tf-text-muted)', width: '14px' }} />
                  <span style={{ fontWeight: '600' }}>{invoice.userId?.name || 'Walk-in Guest'}</span>
               </div>
               <div className="tf-flex" style={{ gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--tf-text-muted)', width: '14px' }} />
                  <span style={{ fontSize: '0.9rem' }}>{invoice.userId?.email || 'N/A'}</span>
               </div>
               <div className="tf-flex" style={{ gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faPhone} style={{ color: 'var(--tf-text-muted)', width: '14px' }} />
                  <span style={{ fontSize: '0.9rem' }}>{invoice.userId?.contact || 'N/A'}</span>
               </div>
            </div>
         </div>

         <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', letterSpacing: '1px', marginBottom: '1.25rem' }}>
               <FontAwesomeIcon icon={faBuilding} /> Issuer
            </h4>
            <div className="tf-grid" style={{ gap: '0.75rem' }}>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '800', margin: 0, color: primaryColor }}>
                    {invoice.restaurantId?.name || 'TastyFlow Culinary'}
                  </h3>
                  {invoice.restaurantId?.invoiceSettings?.contactVisible !== false && (
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--tf-text-muted)', lineHeight: '1.4' }}>
                       {invoice.restaurantId?.location || 'Shlok Infinity, 1st Floor, Sundersingh Bhandari Overbridge Opposite Vishvakarma Temple'}
                    </p>
                  )}
               </div>
               <div className="tf-grid" style={{ gap: '0.4rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.75rem' }}>
                  {invoice.restaurantId?.invoiceSettings?.contactVisible !== false && (
                    <>
                      <div className="tf-flex" style={{ gap: '0.5rem', fontSize: '0.75rem' }}>
                         <FontAwesomeIcon icon={faPhone} style={{ width: '12px', opacity: 0.5 }} />
                         <span>{invoice.restaurantId?.contactNumber || '(909) 91-49101'}</span>
                      </div>
                      <div className="tf-flex" style={{ gap: '0.5rem', fontSize: '0.75rem' }}>
                         <FontAwesomeIcon icon={faEnvelope} style={{ width: '12px', opacity: 0.5 }} />
                         <span>{invoice.restaurantId?.email || 'tastyflow@gmail.com'}</span>
                      </div>
                    </>
                  )}
               </div>
            </div>
         </div>
      </div>

      {/* Reservation Intelligence */}
      {invoice.reservedTableInfo && (
        <div className="tf-card" style={{ padding: '1.5rem', border: '1px solid var(--tf-success)', background: 'rgba(0, 209, 178, 0.02)' }}>
           <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--tf-success)', letterSpacing: '1px', marginBottom: '1.25rem' }}>
              <FontAwesomeIcon icon={faTable} /> Linked Reservation
           </h4>
           <div className="tf-flex" style={{ justifyContent: 'space-between', gap: '2rem', flexWrap: 'wrap' }}>
              <div className="tf-flex" style={{ gap: '1.5rem' }}>
                 <div>
                    <span style={{ fontSize: '0.65rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Surface Unit</span>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Table #{invoice.reservedTableInfo.tableNumber}</div>
                 </div>
                 <div style={{ padding: '0 1.5rem', borderLeft: '1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ fontSize: '0.65rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Temporal Slot</span>
                    <div className="tf-flex" style={{ gap: '0.5rem', fontWeight: '600' }}>
                       <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.8rem', opacity: 0.5 }} />
                       {invoice.reservedTableInfo.slotTime}
                    </div>
                 </div>
                 <div style={{ paddingLeft: '1.5rem', borderLeft: '1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ fontSize: '0.65rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Registry Date</span>
                    <div className="tf-flex" style={{ gap: '0.5rem', fontWeight: '600' }}>
                       <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '0.8rem', opacity: 0.5 }} />
                       {new Date(invoice.reservedTableInfo.date || invoice.invoiceDate).toLocaleDateString()}
                    </div>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* Itemization Table */}
      <div className="tf-card" style={{ padding: '2rem' }}>
         <h4 style={{ fontSize: '0.9rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FontAwesomeIcon icon={faUtensils} style={{ color: primaryColor }} />
            Billed Items
         </h4>
         <div className="tf-list-container">
            <div className="tf-list-header" style={{ gridTemplateColumns: '40px 2fr 100px 100px 140px', gap: '1rem' }}>
               <span>IDX</span>
               <span>DESCRIPTION</span>
               <span style={{ textAlign: 'center' }}>RATE</span>
               <span style={{ textAlign: 'center' }}>QTY</span>
               <span style={{ textAlign: 'right' }}>SUMTOTAL</span>
            </div>
            {invoice.foods.map((food, index) => (
               <div key={index} className="tf-list-item" style={{ gridTemplateColumns: '40px 2fr 100px 100px 140px', gap: '1rem', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem' }}>{String(index + 1).padStart(2, '0')}</span>
                  <span style={{ fontWeight: '600' }}>{food.name}</span>
                  <span style={{ textAlign: 'center', opacity: 0.6 }}>₹{food.price}.00</span>
                  <span style={{ textAlign: 'center', fontWeight: '700' }}>{food.quantity}</span>
                  <span style={{ textAlign: 'right', fontWeight: '800', color: primaryColor }}>₹{(food.quantity * food.price).toFixed(2)}</span>
               </div>
            ))}
         </div>
      </div>
    </div>
  );

  const renderLayout2 = () => (
    <div className="modern-layout" style={{ background: '#fcfcfc', color: '#2c3e50', padding: '0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
      {/* Top Banner */}
      <div style={{ height: '140px', background: primaryColor, position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3rem' }}>
        <div className="tf-flex" style={{ gap: '1.5rem', zIndex: 1 }}>
          {invoice.restaurantId?.logo && invoice.restaurantId?.invoiceSettings?.showLogo && (
            <img src={invoice.restaurantId.logo} alt="Logo" style={{ height: '60px', width: '60px', objectFit: 'contain', borderRadius: '12px', background: 'white', padding: '5px' }} />
          )}
          <div style={{ color: 'white' }}>
            <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '900' }}>{invoice.restaurantId?.name}</h1>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '0.8rem' }}>Modern Dining Experience • Invoice #{invoice.invoiceNumber}</p>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right', color: 'white', zIndex: 1 }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.7 }}>Invoice Date</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{new Date(invoice.invoiceDate).toLocaleDateString()}</div>
        </div>
        {/* Decorative circle */}
        <div style={{ position: 'absolute', right: '-50px', top: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }}></div>
      </div>

      <div style={{ padding: '2.5rem 3rem' }}>
        {/* Info Grid */}
        <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '3rem', marginBottom: '3rem' }}>
          <div>
            <h4 style={{ color: primaryColor, fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '1rem', letterSpacing: '2px' }}>Customer Billing</h4>
            <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>{invoice.userId?.name || 'Walk-in Guest'}</div>
            <div style={{ color: '#7f8c8d', marginTop: '0.4rem', fontSize: '0.9rem' }}>{invoice.userId?.email || 'No Email Provided'}</div>
            <div style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>{invoice.userId?.contact || 'No Contact Provided'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h4 style={{ color: primaryColor, fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '1rem', letterSpacing: '2px' }}>Restaurant Intel</h4>
            <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>{invoice.restaurantId?.name}</div>
            {invoice.restaurantId?.invoiceSettings?.contactVisible && (
              <>
                <div style={{ color: '#7f8c8d', marginTop: '0.4rem', fontSize: '0.85rem' }}>{invoice.restaurantId?.location}</div>
                <div style={{ color: primaryColor, fontSize: '0.85rem', fontWeight: '700', marginTop: '0.2rem' }}>{invoice.restaurantId?.contactNumber}</div>
              </>
            )}
          </div>
        </div>

        {/* Table Content */}
        <div style={{ marginBottom: '3rem' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' }}>
            <thead>
              <tr style={{ color: '#7f8c8d', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                <th style={{ textAlign: 'left', padding: '0 1rem' }}>Description</th>
                <th style={{ textAlign: 'center' }}>Price</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '0 1rem' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.foods.map((food, idx) => (
                <tr key={idx} style={{ background: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', borderRadius: '8px' }}>
                  <td style={{ padding: '1.2rem 1rem', fontWeight: '700', borderRadius: '8px 0 0 8px', borderLeft: `4px solid ${primaryColor}` }}>{food.name}</td>
                  <td style={{ textAlign: 'center', color: '#7f8c8d' }}>₹{food.price}</td>
                  <td style={{ textAlign: 'center', fontWeight: '700' }}>{food.quantity}</td>
                  <td style={{ textAlign: 'right', padding: '0 1rem', fontWeight: '800' }}>₹{(food.price * food.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderLayout3 = () => (
    <div style={{ background: 'white', color: '#333', padding: '3rem', borderRadius: '4px', border: '1px solid #ddd' }}>
      <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4rem' }}>
        <div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '300', margin: 0, letterSpacing: '-1px' }}>INVOICE</h1>
          <div style={{ color: '#999', marginTop: '0.5rem' }}>Ref: #00{invoice.invoiceNumber}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {invoice.restaurantId?.logo && invoice.restaurantId?.invoiceSettings?.showLogo && (
            <img src={invoice.restaurantId.logo} alt="Logo" style={{ height: '40px', grayscale: '100%' }} />
          )}
          <h2 style={{ fontSize: '1.2rem', margin: '0.5rem 0 0' }}>{invoice.restaurantId?.name}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>{new Date(invoice.invoiceDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '4rem', marginBottom: '4rem' }}>
        <div style={{ borderTop: '2px solid #333', paddingTop: '1.5rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '900', color: '#999', marginBottom: '1rem' }}>BILL TO:</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{invoice.userId?.name || 'Walk-in Guest'}</div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>{invoice.userId?.contact || ''}</div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>{invoice.userId?.email || ''}</div>
        </div>
        <div style={{ borderTop: '2px solid #333', paddingTop: '1.5rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '900', color: '#999', marginBottom: '1rem' }}>FROM:</div>
          <div style={{ fontWeight: '700' }}>{invoice.restaurantId?.name}</div>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>{invoice.restaurantId?.location}</p>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #333' }}>
            <th style={{ textAlign: 'left', padding: '1rem 0', fontWeight: '900' }}>Item Description</th>
            <th style={{ textAlign: 'right', padding: '1rem 0' }}>Quantity</th>
            <th style={{ textAlign: 'right', padding: '1rem 0' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.foods.map((food, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '1.5rem 0' }}>
                <div style={{ fontWeight: '700' }}>{food.name}</div>
              </td>
              <td style={{ textAlign: 'right' }}>{food.quantity}</td>
              <td style={{ textAlign: 'right', fontWeight: '700' }}>₹{(food.price * food.quantity).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderLayout4 = () => (
    <div style={{ background: '#fff', color: '#1a1a1a', padding: '2rem', borderRadius: '12px' }}>
      <div style={{ borderBottom: '4px solid #000', paddingBottom: '1.5rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '3px' }}>{invoice.restaurantId?.name}</h2>
          <span style={{ background: '#000', color: '#fff', padding: '2px 8px', fontSize: '0.6rem', fontWeight: '900' }}>COMPACT BILLING</span>
        </div>
        <div style={{ textAlign: 'right' }}>
           <div style={{ fontSize: '1.2rem', fontWeight: '900' }}>#{invoice.invoiceNumber}</div>
           <div style={{ fontSize: '0.7rem' }}>{new Date(invoice.invoiceDate).toDateString()}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ borderRight: '1px solid #eee', paddingRight: '1rem' }}>
          <div style={{ fontSize: '0.5rem', fontWeight: '900', color: '#888' }}>CLIENT</div>
          <div style={{ fontSize: '0.8rem', fontWeight: '700' }}>{invoice.userId?.name || 'Walk-in'}</div>
        </div>
        <div style={{ borderRight: '1px solid #eee', paddingRight: '1rem' }}>
          <div style={{ fontSize: '0.5rem', fontWeight: '900', color: '#888' }}>TABLE</div>
          <div style={{ fontSize: '0.8rem', fontWeight: '700' }}>{invoice.reservedTableInfo?.tableNumber || 'N/A'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.5rem', fontWeight: '900', color: '#888' }}>CONTACT</div>
          <div style={{ fontSize: '0.8rem', fontWeight: '700' }}>{invoice.restaurantId?.contactNumber}</div>
        </div>
      </div>

      <div style={{ background: '#f5f5f5', padding: '1rem' }}>
        {invoice.foods.map((food, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ fontSize: '0.85rem' }}>
              <span style={{ fontWeight: '800' }}>{food.quantity}x</span> {food.name}
            </div>
            <div style={{ fontWeight: '700' }}>₹{(food.price * food.quantity).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="tf-invoice-container" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Top Action Bar */}
      <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '0 0 1rem 0', borderBottom: '1px solid var(--tf-border)' }}>
         <div className="tf-flex" style={{ gap: '1rem' }}>
            <div style={{ width: '45px', height: '45px', borderRadius: '12px', background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
               <FontAwesomeIcon icon={faReceipt} />
            </div>
            <div>
               <h2 className="tf-heading-xs" style={{ margin: 0 }}>Bill Preview #{invoice.invoiceNumber}</h2>
               <div className="tf-flex" style={{ gap: '0.5rem', marginTop: '0.2rem', color: 'var(--tf-text-muted)', fontSize: '0.75rem' }}>
                  <span><FontAwesomeIcon icon={faShieldHalved} /> Secured</span>
               </div>
            </div>
         </div>

         <div className="tf-flex" style={{ gap: '0.75rem' }}>
            <button onClick={printInvoice} className="tf-btn" style={{ background: 'rgba(255,255,255,0.05)', color: 'white' }}>
               <FontAwesomeIcon icon={faPrint} />
            </button>
            <button onClick={sendInvoice} disabled={isSending || !invoice.userId} className="tf-btn tf-btn-primary" title={!invoice.userId ? 'Walk-in customers do not have an email on file' : ''}>
               {isSending ? 'Sending...' : (!invoice.userId ? 'No Email (Walk-in)' : 'Dispatch')}
            </button>
         </div>
      </div>

      {/* Dynamic Layout Rendering */}
      {layoutId === '1' && renderLayout1()}
      {layoutId === '2' && renderLayout2()}
      {layoutId === '3' && renderLayout3()}
      {layoutId === '4' && renderLayout4()}

      {/* Shared Header Message */}
      {invoice.restaurantId?.invoiceSettings?.headerText && layoutId !== '2' && layoutId !== '3' && (
        <div style={{ 
          background: `${accentColor}15`, 
          borderLeft: `4px solid ${accentColor}`,
          padding: '1rem 1.5rem',
          fontSize: '0.9rem',
          fontWeight: '700',
          color: accentColor,
          borderRadius: '0 12px 12px 0'
        }}>
            <FontAwesomeIcon icon={faCircleInfo} style={{marginRight: '0.75rem', opacity: 0.8}} />
            {invoice.restaurantId.invoiceSettings.headerText}
        </div>
      )}

      {/* Shared Totals Grid (Only if not already in layout) */}
      <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
          <div className="tf-card" style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)' }}>
             <p style={{ margin: 0, fontSize: '0.6rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>CGST</p>
             <div style={{ fontSize: '1rem', fontWeight: '700' }}>₹{invoice.cgst?.toFixed(2) || '0.00'}</div>
          </div>
          <div className="tf-card" style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)' }}>
             <p style={{ margin: 0, fontSize: '0.6rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>SGST</p>
             <div style={{ fontSize: '1rem', fontWeight: '700' }}>₹{invoice.sgst?.toFixed(2) || '0.00'}</div>
          </div>
          <div className="tf-card" style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)' }}>
             <p style={{ margin: 0, fontSize: '0.6rem', textTransform: 'uppercase', color: 'var(--tf-error)', fontWeight: '700' }}>Disc.</p>
             <div style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--tf-error)' }}>-₹{invoice.discount.toFixed(2)}</div>
          </div>
          <div className="tf-card" style={{ padding: '1rem', background: 'var(--tf-bg-input)', borderLeft: `4px solid ${primaryColor}` }}>
             <p style={{ margin: 0, fontSize: '0.6rem', textTransform: 'uppercase', color: primaryColor, fontWeight: '800' }}>Payable</p>
             <div style={{ fontSize: '1.4rem', fontWeight: '900' }}>₹{invoice.totalAmount.toFixed(2)}</div>
          </div>
      </div>

      <div style={{ padding: '1.25rem', background: `${primaryColor}10`, borderRadius: '12px', borderLeft: `4px solid ${primaryColor}` }}>
          <div className="tf-flex" style={{ gap: '0.75rem' }}>
             <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: primaryColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>
                <FontAwesomeIcon icon={faCircleCheck} />
             </div>
             <div>
                <p style={{ margin: '0', fontWeight: '600', color: 'var(--tf-text-white)', textTransform: 'capitalize', fontSize: '0.85rem' }}>{toWords(invoice.totalAmount)} Rupees only</p>
             </div>
          </div>
      </div>

      {/* Custom Footer Note */}
      <div style={{ marginTop: '1rem', textAlign: 'center', padding: '1rem', borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--tf-text-muted)', fontStyle: 'italic' }}>
             {invoice.restaurantId?.invoiceSettings?.footerText || "We appreciate your choice. Thank you for dining with us!"}
          </p>
      </div>
    </div>
  );
};

export default Invoice;
